import { ref, watch } from 'vue'
import api from '@/lib/api'
import Cookies from 'js-cookie'
import { API_HOST, APP_NAME, ORG_ID } from '@/lib/utils'

const _isRefreshing = ref(false)
const _token = ref<string>()
const _refreshToken = ref<string>()

/** Returns the current token. */
export function getToken() {
  return _token.value
}

/** Fetches the authenticated user's data. */
export async function getUser() {
  const response = await api.getSelf()

  if (response?.status === false) {
    try {
      await refreshToken()
    } catch (e) {
      clearTokens()
    } finally {
      window.location.reload()
    }
  }
  return response?.user || response
}

/** Initializes the authentication state. */
export async function initAuth(accessCode?: string) {
  api.init(API_HOST || '', Number(ORG_ID) || 0)
  _token.value = Cookies.get('vssl-token') || undefined
  _refreshToken.value = Cookies.get('vssl-refresh-token') || undefined

  if (accessCode) {
    try {
      const { accessToken, refreshToken } = await api.getTokens(accessCode)
      setToken(accessToken)
      setRefresh(refreshToken)
    } catch (e) {
      console.error('Failed to get tokens:', e)
      clearTokens()
    }
  }
}

/** Parses the payload from a JWT token. */
function getTokenPayload(token: string) {
  try {
    return JSON.parse(atob(token?.split('.')[1] || ''))
  } catch (e) {
    console.error('Failed to parse token payload:', e)
    return null
  }
}

/** Creates a timeout for refreshing the token when it is half way through its life. */
function createRefreshTimeout(token: string) {
  const tokenPayload = getTokenPayload(token)
  if (!tokenPayload) {
    return
  }

  const { iat = 0, exp = 0 } = tokenPayload
  const halfLife = (exp - iat) / 2
  const timeUntilRefresh = (halfLife * 1000) - (Date.now() - iat * 1000)
  return setTimeout(refreshToken, timeUntilRefresh)
}

/** Refreshes the token when it is half way through its life. */
let refreshTimeout: ReturnType<typeof setTimeout> | undefined
watch(_token, (token) => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout)
  }

  if (token) {
    refreshTimeout = createRefreshTimeout(token)
  }
})

/** Sets the current token. */
function setToken(token: string) {
  _token.value = token
  Cookies.set('vssl-token', _token.value)
}

/** Sets the current refresh token. */
function setRefresh(token: string) {
  _refreshToken.value = token
  Cookies.set('vssl-refresh-token', _refreshToken.value)
}

/** Clears the current tokens. */
export function clearTokens() {
  _token.value = undefined
  _refreshToken.value = undefined
  Cookies.remove('vssl-token')
  Cookies.remove('vssl-refresh-token')
}

/** Attempt a refresh of the current token. */
type RefreshTokenResponse = { accessToken: string; refreshToken: string }
let refreshPromise: Promise<RefreshTokenResponse> | null = null
async function refreshToken(refresh: string = _refreshToken.value || '') {
  if (!refresh) throw new Error('No refresh token provided')

  _isRefreshing.value = true
  try {
    refreshPromise = api.refreshToken(refresh)
    const { accessToken, refreshToken } = await refreshPromise
    setToken(accessToken)
    setRefresh(refreshToken)
    return { accessToken, refreshToken }
  } catch (e: any) {
    clearTokens()

    if (!e?.response) {
      throw new Error(`Unable to access the ${APP_NAME} server.`)
    } else {
      throw e?.response?.data?.error || 'Unknown authentication renewal error.'
    }
  } finally {
    _isRefreshing.value = false
  }
}

/** Ensure the current token is refreshed, or wait for it to refresh. */
export async function ensureFreshToken() {
  if (_isRefreshing.value) {
    const { accessToken } = await refreshPromise || {}
    return accessToken || _token.value
  } else {
    return _token.value
  }
}
